import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { CustomLunch } from "../@data";
import {
  getAllCustomLunchs,
  getCustomLunchsByDate,
  getCustomLunchsByProfessionalAndDate,
} from "../services/custom-lunchs";
import { useNotification } from "./notification";

export interface CustomLunchsContextModel {
  customLunchs: CustomLunch[];
  loading: boolean;
  currentDate: string | null;
  fetchCustomLunchs: () => void;
  fetchCustomLunchsByDate: (date: string) => void;
  fetchCustomLunchsByProfessionalAndDate: (
    professionalId: string,
    date: string
  ) => Promise<void>;
}

const CustomLunchsContext = createContext<CustomLunchsContextModel>(
  {} as CustomLunchsContextModel
);

export const CustomLunchsProvider = ({ children }: { children: ReactNode }) => {
  const [customLunchs, setCustomLunchs] = useState<CustomLunch[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState<string | null>(null);
  const { handleNotification } = useNotification();

  const fetchCustomLunchs = useCallback(() => {
    setLoading(true);
    getAllCustomLunchs()
      .then((data) => setCustomLunchs(data))
      .catch((error: any) => {
        handleNotification("error", "Erro ao buscar almoços customizados");
      })
      .finally(() => setLoading(false));
  }, [handleNotification]);

  const fetchCustomLunchsByDate = useCallback(
    (date: string) => {
      setLoading(true);
      setCurrentDate(date);
      getCustomLunchsByDate(date)
        .then((data) => setCustomLunchs(data))
        .catch((error: any) => {
          handleNotification(
            "error",
            "Erro ao buscar almoços customizados por data"
          );
        })
        .finally(() => setLoading(false));
    },
    [handleNotification]
  );

  const fetchCustomLunchsByProfessionalAndDate = useCallback(
    async (professionalId: string, date: string) => {
      setLoading(true);
      setCurrentDate(date);
      try {
        const data = await getCustomLunchsByProfessionalAndDate(
          professionalId,
          date
        );
        setCustomLunchs(data);
      } catch (error: any) {
        handleNotification(
          "error",
          "Erro ao buscar almoços customizados do profissional"
        );
      } finally {
        setLoading(false);
      }
    },
    [handleNotification]
  );

  return (
    <CustomLunchsContext.Provider
      value={{
        customLunchs,
        loading,
        currentDate,
        fetchCustomLunchs,
        fetchCustomLunchsByDate,
        fetchCustomLunchsByProfessionalAndDate,
      }}
    >
      {children}
    </CustomLunchsContext.Provider>
  );
};

export const useCustomLunchs = () => {
  const context = useContext(CustomLunchsContext);
  if (!context) {
    throw new Error(
      "useCustomLunchs deve ser usado dentro de um CustomLunchsProvider"
    );
  }
  return context;
};
